import {
  FetchMaintenancesResponse,
  initialMaintenanceContract,
  MaintenanceContract,
  MaintenanceContractKey,
} from '@api/models'
import { toast, useModal } from '@components/common'
import {
  useCreateMaintenanceMutation,
  useDeleteMaintenanceMutation,
  useEditMaintenanceMutation,
  useFetchMaintenancesQuery,
  useUploadAttachmentMutation,
  useDeleteAttachmentMutation,
} from '@api'
import { Dispatch, SetStateAction, useState } from 'react'
import { useTableParams } from '@hooks'
import { TableParamsHook } from 'hooks/useTableParams'
import {
  StagedAttachment,
  MaintenanceFormSubmissionData,
} from '@api/attachments/models/Attachment.model'
import { useAppDispatch } from '../../index'
import { attachmentsEndpoints } from '@api/attachments/attachmentsEndpoints'

export interface MaintenancesHook extends TableParamsHook {
  filterState: Object
  setFilterState: Dispatch<SetStateAction<{}>>
  searchKey: string
  selectedMaintenance: MaintenanceContract

  maintenances: FetchMaintenancesResponse
  isLoadingMaintenances: boolean
  isErrorFetchingMaintenances: boolean
  isRefetchingMaintenances: boolean

  handleEditMaintenance: (data: MaintenanceFormSubmissionData) => Promise<void>
  handleCreateMaintenance: (data: MaintenanceFormSubmissionData) => Promise<void>
  isLoadingEditMaintenance: boolean
  isLoadingCreateMaintenance: boolean

  handleDeleteMaintenance: (payload: MaintenanceContract) => Promise<void>
  isLoadingDeleteMaintenance: boolean

  deleteMaintenanceModalIsOpen: boolean
  triggerDeleteMaintenanceModal
  closeDeleteMaintenanceModal
  openDeleteMaintenanceModal: (maintenance: MaintenanceContract) => void
  openEditMaintenanceModal: (maintenance: MaintenanceContract) => void
  triggerEditMaintenanceModal
  closeEditMaintenanceModal
  editMaintenanceModalIsOpen: boolean

  openCreateMaintenanceModal: () => void
  createMaintenanceModalIsOpen: boolean
  closeCreateMaintenanceModal
}

export default function useMaintenances(): MaintenancesHook {
  const [filterState, setFilterState] = useState({})
  const [selectedMaintenance, setSelectedMaintenance] = useState<MaintenanceContract>(
    undefined
  )
  const dispatch = useAppDispatch()

  const searchKey: MaintenanceContractKey = 'MN_DESC'
  const initialItemsPerPage = 20

  // Use useTableParams hook for table filtering, pagination, etc.
  const {
    pageNumber,
    itemsPerPage,
    fetchRequestPayload,
    handleSearch,
    searchValue,
    changePage,
    sortByInitialState,
    updateSortSearchParam,
  } = useTableParams({
    searchKey,
    filterState,
    setFilterState,
    initialItemsPerPage,
  })

  // Fetch Maintenances
  const {
    data: maintenances,
    isLoading: isLoadingMaintenances,
    isError: isErrorFetchingMaintenances,
    isFetching: isRefetchingMaintenances,
  } = useFetchMaintenancesQuery(fetchRequestPayload)

  // MODAL STATES
  // Delete modal
  const {
    modalIsOpen: deleteMaintenanceModalIsOpen,
    triggerModal: triggerDeleteMaintenanceModal,
    closeModal: closeDeleteMaintenanceModal,
  } = useModal()
  function openDeleteMaintenanceModal(maintenance: MaintenanceContract): void {
    setSelectedMaintenance(maintenance)
    triggerDeleteMaintenanceModal()
  }

  // Edit modal
  const {
    modalIsOpen: editMaintenanceModalIsOpen,
    triggerModal: triggerEditMaintenanceModal,
    closeModal: closeEditMaintenanceModal,
  } = useModal()

  function openEditMaintenanceModal(maintenance: MaintenanceContract): void {
    setSelectedMaintenance(maintenance)
    triggerEditMaintenanceModal()
  }

  // Create modal
  const {
    modalIsOpen: createMaintenanceModalIsOpen,
    triggerModal: triggerCreateMaintenanceModal,
    closeModal: closeCreateMaintenanceModal,
  } = useModal()
  function openCreateMaintenanceModal(): void {
    setSelectedMaintenance(initialMaintenanceContract)
    triggerCreateMaintenanceModal()
  }

  // MUTATIONS
  // Delete
  const [
    deleteMaintenance,
    { isLoading: isLoadingDeleteMaintenance },
  ] = useDeleteMaintenanceMutation()
  const [deleteAttachment] = useDeleteAttachmentMutation()

  async function handleDeleteMaintenance(payload: MaintenanceContract): Promise<void> {
    try {
      const getMaintenanceAttachmentsResult = await dispatch(
        attachmentsEndpoints.endpoints.getMaintenanceAttachments.initiate({
          maintenanceID: payload.MN_CON_ID,
        })
      )

      const attachments = getMaintenanceAttachmentsResult.data || []

      await deleteMaintenance(payload).unwrap()
      closeDeleteMaintenanceModal()

      if (attachments.length > 0) {
        const attachmentDeletionPromises = attachments.map(attachment =>
          deleteAttachment({
            id: attachment.ID,
            type: 'Maintenance',
          }).unwrap()
        )

        try {
          await Promise.all(attachmentDeletionPromises)
          toast.success(
            `Successfully deleted "${payload.MN_NAME}" and ${attachments.length} associated attachment(s) from your maintenance contracts.`
          )
        } catch (attachmentError) {
          console.warn('Some attachments failed to delete:', attachmentError)
          toast.success(
            `Successfully deleted "${payload.MN_NAME}" from your maintenance contracts, but ${attachments.length} attachment(s) could not be removed.`
          )
        }
      } else {
        toast.success(
          `Successfully deleted "${payload.MN_NAME}" from your maintenance contracts.`
        )
      }
    } catch (error: any) {
      toast.error(error.data || 'There was an unexpected error.')
    }
  }

  // Edit
  const [
    editMaintenance,
    { isLoading: isLoadingEditMaintenance },
  ] = useEditMaintenanceMutation()
  const [uploadAttachment] = useUploadAttachmentMutation()

  async function uploadStagedAttachments(
    maintenanceID: string,
    stagedAttachments: StagedAttachment[]
  ): Promise<{ successful: number; failed: StagedAttachment[] }> {
    const results = { successful: 0, failed: [] as StagedAttachment[] }

    const attachmentsToUpload = stagedAttachments.slice(0, 1)

    for (const staged of attachmentsToUpload) {
      try {
        const formData = new FormData()
        formData.append('file', staged.file)
        formData.append('assetCompanyID', '0') // Not needed for maintenance
        formData.append('objectIDs', JSON.stringify([maintenanceID]))
        formData.append('type', 'Maintenance')
        formData.append('comment', staged.comment)

        await uploadAttachment(formData).unwrap()
        results.successful++
      } catch (error) {
        results.failed.push(staged)
      }
    }

    if (stagedAttachments.length > 1) {
      results.failed.push(...stagedAttachments.slice(1))
    }

    return results
  }

  async function handleEditMaintenance(data: MaintenanceFormSubmissionData): Promise<void> {
    try {
      // Step 1: Update the maintenance contract
      await editMaintenance(data.maintenanceData).unwrap()

      // Step 2: Upload staged attachments if any (for editing, we still allow adding new attachments)
      if (data.stagedAttachments.length > 0) {
        const uploadResults = await uploadStagedAttachments(
          data.maintenanceData.MN_CON_ID,
          data.stagedAttachments
        )

        if (uploadResults.failed.length > 0) {
          toast.error(
            `Maintenance contract updated successfully, but ${uploadResults.failed.length} attachment(s) failed to upload.`
          )
        } else if (uploadResults.successful > 0) {
          toast.success(
            `Maintenance contract "${data.maintenanceData.MN_DESC}" successfully updated with ${uploadResults.successful} new attachment(s).`
          )
        } else {
          toast.success(
            `Maintenance contract "${data.maintenanceData.MN_DESC}" successfully updated.`
          )
        }
      } else {
        toast.success(
          `Maintenance contract "${data.maintenanceData.MN_DESC}" successfully updated.`
        )
      }

      closeEditMaintenanceModal()
    } catch (error: any) {
      toast.error(
        error.data || 'There was an unexpected error updating this maintenance contract.'
      )
    }
  }

  // Create
  const [
    createMaintenance,
    { isLoading: isLoadingCreateMaintenance },
  ] = useCreateMaintenanceMutation()

  async function handleCreateMaintenance(
    data: MaintenanceFormSubmissionData
  ): Promise<void> {
    try {
      // Step 1: Create the maintenance contract
      await createMaintenance(data.maintenanceData).unwrap()

      // Step 2: Upload staged attachments if any
      if (data.stagedAttachments.length > 0) {
        const uploadResults = await uploadStagedAttachments(
          data.maintenanceData.MN_CON_ID,
          data.stagedAttachments
        )

        if (uploadResults.failed.length > 0) {
          toast.error(
            `Maintenance contract created successfully, but ${uploadResults.failed.length} attachment(s) failed to upload.`
          )
        } else if (uploadResults.successful > 0) {
          toast.success(
            `Successfully added ${data.maintenanceData.MN_DESC} to maintenance contracts with ${uploadResults.successful} attachment(s).`
          )
        } else {
          toast.success(
            `Successfully added ${data.maintenanceData.MN_DESC} to maintenance contracts.`
          )
        }
      } else {
        toast.success(
          `Successfully added ${data.maintenanceData.MN_DESC} to maintenance contracts.`
        )
      }

      closeCreateMaintenanceModal()
    } catch (error: any) {
      toast.error(
        error.data || 'There was an unexpected error creating the maintenance contract.'
      )
    }
  }

  return {
    // tableParams return
    pageNumber,
    itemsPerPage,
    fetchRequestPayload,
    handleSearch,
    searchValue,
    changePage,
    sortByInitialState,
    updateSortSearchParam,
    //
    filterState,
    setFilterState,
    searchKey,
    selectedMaintenance,

    maintenances,
    isLoadingMaintenances,
    isErrorFetchingMaintenances,
    isRefetchingMaintenances,

    handleDeleteMaintenance,
    isLoadingDeleteMaintenance,

    handleEditMaintenance,
    handleCreateMaintenance,
    isLoadingCreateMaintenance,
    isLoadingEditMaintenance,

    deleteMaintenanceModalIsOpen,
    triggerDeleteMaintenanceModal,
    closeDeleteMaintenanceModal,
    openDeleteMaintenanceModal,
    openEditMaintenanceModal,
    triggerEditMaintenanceModal,
    closeEditMaintenanceModal,
    editMaintenanceModalIsOpen,

    createMaintenanceModalIsOpen,
    openCreateMaintenanceModal,
    closeCreateMaintenanceModal,
  }
}
